import { forwardRef, useRef } from "react";
import { motion, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

interface FinalCTAProps {
  style?: React.CSSProperties;
}

// Final CTA Section - Bold & Vibrant Convergence
export const FinalCTA = forwardRef<HTMLDivElement, FinalCTAProps>(
  ({ style }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Scroll-based gradient animation
    const { scrollYProgress } = useScroll({
      target: containerRef,
      offset: ["start 70%", "end 30%"],
    });

    // Map scroll progress to gradient position percentage
    const gradientX = useTransform(scrollYProgress, [0, 1], [0, -100]);
    const gradientPosition = useMotionTemplate`${gradientX}% 0%`;

    // Merge refs
    const setRefs = (el: HTMLDivElement | null) => {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    };

    return (
      <div ref={setRefs} style={style} className="py-24 sm:py-32 relative overflow-hidden">
        {/* Multi-layered gradient background - all three brand colors converging */}
        <div className="absolute inset-0 -z-10">
          {/* Base purple layer - manage domain */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(var(--primary-rgb), 0.15), transparent)'
            }}
          />
          {/* Orange accent - grow domain (top right) */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at 80% 20%, rgba(var(--grow-rgb), 0.12), transparent 50%)'
            }}
          />
          {/* Teal accent - business domain (bottom left) */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at 20% 80%, rgba(var(--secondary-rgb), 0.1), transparent 50%)'
            }}
          />
          {/* Noise texture overlay for depth */}
          <div className="absolute inset-0 bento-noise opacity-[0.015]" />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          {/* Main headline - action-focused with gradient emphasis */}
          <h2 className="mx-auto max-w-4xl font-bold tracking-tight mb-8 text-4xl sm:text-6xl lg:text-8xl">
            <span className="whitespace-nowrap">Stop juggling tools.</span>
            <br />
            <motion.span
              style={{
                display: 'inline-block',
                background: 'linear-gradient(to right, var(--primary-hex), var(--grow-hex), var(--secondary-hex), var(--primary-hex))',
                backgroundSize: '300% 100%',
                backgroundPosition: gradientPosition,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                paddingBottom: '0.1em',
                lineHeight: 1.2,
              }}
            >
              Start growing.
            </motion.span>
          </h2>

          {/* Supporting copy - pain points & benefits */}
          <p className="mx-auto mt-6 max-w-2xl text-lg sm:text-xl leading-relaxed text-muted-foreground">
            One platform for scheduling, payroll, customer loyalty, and analytics.
          </p>

          {/* CTA Button - pricing action */}
          <div className="mt-12 flex items-center justify-center">
            <Button
              size="lg"
              className="px-10 py-7 text-lg sm:text-xl font-semibold rounded-xl shadow-lg group relative overflow-hidden border-0"
              asChild
            >
              <Link to="/pricing">
                {/* Scroll-driven gradient background */}
                <motion.span
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to right, var(--primary-hex), var(--grow-hex), var(--secondary-hex), var(--primary-hex))',
                    backgroundSize: '300% 100%',
                    backgroundPosition: gradientPosition,
                  }}
                />
                {/* Button content */}
                <span className="relative z-10 text-white flex items-center gap-3">
                  Start Free Today
                  <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Button>
          </div>

          {/* Trust indicators - integrated with brand colors */}
          <div className="mt-16 pt-8 border-t border-border/30">
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-10">
              <div className="flex items-center gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-primary shadow-sm shadow-primary/50" />
                <span className="text-sm font-medium text-foreground">Free review tools</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-grow shadow-sm shadow-grow/50" />
                <span className="text-sm font-medium text-foreground">No setup fees</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-secondary shadow-sm shadow-secondary/50" />
                <span className="text-sm font-medium text-foreground">Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

FinalCTA.displayName = "FinalCTA";
